import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, Text, View } from 'react-native';

import { theme } from '@garnish/constants';
import { DisplayText, Icon, TextSpace, useResponsive } from '@garnish/core';
import { capitalizeFirstLetter } from '@garnish/forms';

import { ICON_SIZE, ICON_SIZE_SMALLER } from '../utils';

export const OrderPlaybackLocation = (props: OrderPlaybackLocationProps) => {
  const { orderChannel, locationName } = props;
  const connector = getConnectorByChannel(orderChannel);

  const { minWidth, currentHeight } = useResponsive();
  const withSmallerFont = minWidth.isSM && currentHeight.isSM;
  const fontSize: '16' | '24' = withSmallerFont ? '16' : '24';

  return (
    <View style={styles.row}>
      <View style={withSmallerFont ? styles.iconSmaller : styles.icon}>
        <Icon
          name="IconOrderPlaybackLocation"
          color={theme.colors.KALE}
          width={withSmallerFont ? ICON_SIZE_SMALLER : ICON_SIZE}
          height={withSmallerFont ? ICON_SIZE_SMALLER : ICON_SIZE}
        />
      </View>

      <View style={styles.textContainer}>
        <Text>
          <DisplayText style={styles.text} bold sizeMatch={[fontSize]}>
            {capitalizeFirstLetter(orderChannel)}
          </DisplayText>

          <TextSpace />

          <DisplayText style={styles.text} sizeMatch={[fontSize]}>
            <FormattedMessage {...connector} />
          </DisplayText>
        </Text>

        <DisplayText style={styles.text} bold sizeMatch={[fontSize]}>
          {locationName}
        </DisplayText>
      </View>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getConnectorByChannel = (channel: string) => {
  if (channel === 'pickup') {
    return messages.from;
  }

  if (channel === 'delivery') {
    return messages.to;
  }

  return messages.at;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  from: {
    defaultMessage: 'from',
    description: 'Bag | Order playback | From',
  },
  to: {
    defaultMessage: 'to',
    description: 'Bag | Order playback | To',
  },
  at: {
    defaultMessage: 'at',
    description: 'Bag | Order playback | At',
  },
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  textContainer: {
    flexShrink: 1,
  },
  text: {
    color: theme.colors.KALE,
  },
  icon: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    marginBottom: theme.spacing['1'],
  },
  iconSmaller: {
    width: ICON_SIZE_SMALLER,
    height: ICON_SIZE_SMALLER,
    marginBottom: theme.spacing['1'],
  },
  row: {
    flexDirection: 'row',
    gap: theme.spacing['3'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderPlaybackLocationProps = {
  orderChannel: string;
  locationName: string;
};
